import { Link } from "wouter";
import { FileText, Clock, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LazySection } from "@/components/LazySection";
import { AIRecommendations } from "@/components/AIRecommendations";
import type { User as UserType, Article } from "@shared/schema";
import { useMediaCategories } from "@/hooks/useMediaCategories";
import bookCoverImage from "@assets/Chief_in_Tech_Book_Cover_2025-2_1765655498601.png";

interface ContentTabProps {
  user: UserType;
  articles: Article[];
  articlesLoading: boolean;
}

function ArticleCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <Skeleton className="h-40 w-full rounded-md mb-3" />
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-6 w-full" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </CardContent>
    </Card>
  );
}

export function ContentTab({ user, articles, articlesLoading }: ContentTabProps) {
  const { getCategoryLabel } = useMediaCategories();
  const displayArticles = articles || [];

  return (
    <div className="space-y-8">
      <LazySection
        className=""
        minHeight="200px"
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-40 w-full rounded-md" />
              ))}
            </div>
          </div>
        }
      >
        <AIRecommendations />
      </LazySection>

      <section data-testid="section-content-articles">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">For You</h2>
            <p className="text-muted-foreground">Personalized content based on your interests</p>
          </div>
          <Button variant="outline" asChild data-testid="button-view-all-content">
            <Link href="/media">
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {articlesLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <ArticleCardSkeleton key={i} />)}
          </div>
        ) : displayArticles.length === 0 ? (
          <Card data-testid="empty-articles">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No articles yet</p>
                <p className="text-sm text-muted-foreground mt-1">Check back soon for personalized content.</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link href="/media" data-testid="link-browse-content">Browse Content</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayArticles.slice(0, 6).map((article: any) => (
              <Link key={article.id} href={`/articles/${article.slug || article.id}`}>
                <Card className="h-full hover-elevate cursor-pointer" data-testid={`card-article-${article.id}`}>
                  <CardHeader className="pb-3">
                    <div className="h-40 w-full rounded-md bg-muted mb-3 overflow-hidden">
                      {article.featuredImage || article.coverImageUrl ? (
                        <img
                          src={article.featuredImage || article.coverImageUrl}
                          alt={article.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <FileText className="h-12 w-12 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="secondary" className="no-default-active-elevate" data-testid={`badge-category-${article.id}`}>
                        {getCategoryLabel(article.category || 'culture')}
                      </Badge>
                      {article.isAiGenerated && (
                        <Badge variant="outline" className="border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-300 no-default-active-elevate" data-testid={`badge-ai-${article.id}`}>
                          <Sparkles className="h-3 w-3 mr-0.5" />
                          AI
                        </Badge>
                      )}
                      {article.readTime && (
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {article.readTime} min
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-lg leading-snug line-clamp-2" data-testid={`text-article-title-${article.id}`}>
                      {article.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base line-clamp-2" data-testid={`text-article-excerpt-${article.id}`}>
                      {article.excerpt}
                    </CardDescription>
                    <div className="mt-4 flex items-center text-secondary font-medium text-sm">
                      Read more
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section data-testid="section-content-book">
        <Card className="bg-gradient-to-r from-cit-primary/5 to-cit-accent/5 border-cit-primary/20 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-cit-primary mb-2">NEW RESOURCE</p>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">Chief in Tech Book</h2>
                  <p className="text-base text-muted-foreground">Breaking the Silicon Ceiling and Leading with Impact</p>
                </div>
                <p className="text-sm leading-relaxed text-foreground">
                  Packed with career strategies and executive insights from Fortune 50 C-level leaders. Learn proven strategies for navigating transitions, building influence, negotiating for your worth, and creating better work-life integration.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild>
                    <a href="https://www.amazon.com/Chief-Tech-Anna-Radulovski/dp/139429266X" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Order on Amazon
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <Link href="/book">
                      View All Options
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardContent className="pt-6 flex items-center justify-center">
              <img
                src={bookCoverImage}
                alt="Chief in Tech Book Cover"
                className="max-h-72 w-auto"
                loading="lazy"
                data-testid="img-content-book-cover"
              />
            </CardContent>
          </div>
        </Card>
      </section>
    </div>
  );
}
